import { useState } from 'react';
import type { InventoryItem, InventoryCategory, Brand, Warehouse, Branch } from '../types/inventory.types';
import { InventoryService, ExcelService } from '../services';

interface UseInventoryExcelExportProps {
  companyId: string | null;
  searchTerm: string;
  selectedCategory: InventoryCategory | null;
  selectedBrand: Brand | null;
  selectedWarehouse: Warehouse | null;
  selectedBranch: Branch | null;
  hideEmptyStock: boolean;
  hideInactiveItems: boolean;
  hideZeroPrice: boolean;
  // ✅ Gelişmiş filtreler (liste ile parity — opsiyonel)
  minSalePrice?: number | null;
  maxSalePrice?: number | null;
  minQuantity?: number | null;
  maxQuantity?: number | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  ecommerceFilter?: 'all' | 'yes' | 'no';
  onlyWithoutImage?: boolean;
  itemClasses?: string[];
  productionMode?: string | null;
  includeProductionColumns?: boolean;
  getCategoryName: (categoryId: string | null) => string;
  getBrandName: (brandId: string | null) => string;
  getWarehouseName: (warehouseId: string | null) => string;
  getBranchName: (branchId: string | null) => string;
  getCurrencySymbol: (currency: string) => string;
  showSnackbar: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
  setError: (error: string | null) => void;
}

export const useInventoryExcelExport = ({
  companyId,
  searchTerm,
  selectedCategory,
  selectedBrand,
  selectedWarehouse,
  selectedBranch,
  hideEmptyStock,
  hideInactiveItems,
  hideZeroPrice,
  minSalePrice,
  maxSalePrice,
  minQuantity,
  maxQuantity,
  createdFrom,
  createdTo,
  ecommerceFilter = 'all',
  onlyWithoutImage = false,
  itemClasses = [],
  productionMode = null,
  includeProductionColumns = false,
  getCategoryName,
  getBrandName,
  getWarehouseName,
  getBranchName,
  getCurrencySymbol,
  showSnackbar,
  setError,
}: UseInventoryExcelExportProps) => {
  const [loadingExport, setLoadingExport] = useState(false);

  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      console.log('📊 [EXCEL EXPORT] Excel dışa aktarma başladı');
      
      // ✅ SQL'den tüm ürünleri çek
      if (!companyId) {
        throw new Error('Şirket ID bulunamadı');
      }
      
      console.log('🔄 [EXCEL EXPORT] SQL\'den ürünler yükleniyor...');
      const sqlItems = (itemClasses.length > 0 || productionMode)
        ? (await InventoryService.getPagedItemsSQL(
            companyId,
            1,
            100000,
            undefined,
            undefined,
            undefined,
            selectedWarehouse?.id,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            true,
            {
              itemClasses: itemClasses.length > 0 ? itemClasses : undefined,
              productionMode: productionMode || undefined,
            }
          )).items
        : await InventoryService.getAllItemsSQL(companyId);
      console.log('✅ [EXCEL EXPORT] SQL\'den yüklenen ürün sayısı:', sqlItems.length);
      
      // ✅ SQL DTO'larını InventoryItem tipine dönüştür
      const convertedItems: InventoryItem[] = sqlItems.map(dto => ({
        ...dto,
        id: dto.id,
        companyId: dto.companyId,
        name: dto.name,
        stockCode: dto.stockCode,
        barcode: dto.barcode || '',
        categoryId: dto.categoryId || null,
        brandId: dto.brandId || null,
        branchesId: dto.branchesId || null,
        unit: dto.unit,
        purchasePrice: dto.purchasePrice || 0,
        purchaseCurrency: dto.purchaseCurrency || 'TRY',
        salePrice: dto.salePrice,
        saleCurrency: dto.saleCurrency,
        listPrice: dto.listPrice || undefined,
        listCurrency: dto.listCurrency || undefined,
        description: dto.description || '',
        imageUrl: dto.imageUrl || null,
        vatRate: dto.vatRate,
        vatStatus: dto.vatStatus,
        sctRate: dto.sctRate || undefined,
        sctStatus: dto.sctStatus || undefined,
        isActive: dto.isActive,
        isEcommerce: dto.isEcommerce || false,
        isQRMenu: false, // SQL'de yok, varsayılan false
        createdAt: dto.createdAt,
        updatedAt: dto.updatedAt || dto.createdAt,
        warehouseStocks: dto.warehouseStocks || [],
        itemClass: (dto as any).itemClass ?? (dto as any).ItemClass ?? null,
        productionMode: (dto as any).productionMode ?? (dto as any).ProductionMode ?? 'SALE_EXPLODE',
      } as InventoryItem));
      
      // Filtreleme uygula (arama, marka, depo, şube, stok durumu, aktiflik)
      let itemsToExport = [...convertedItems];
      
      // Arama terimine göre filtrele
      if (searchTerm.trim() !== '') {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        itemsToExport = itemsToExport.filter(item => {
          return item.name.toLowerCase().includes(lowerCaseSearchTerm) ||
            (item.barcode && item.barcode.includes(searchTerm)) ||
            item.stockCode.toLowerCase().includes(lowerCaseSearchTerm) ||
            getBrandName(item.brandId || null).toLowerCase().includes(lowerCaseSearchTerm);
        });
      }
      
      // Seçilen kategoriye göre filtreleme
      if (selectedCategory) {
        itemsToExport = itemsToExport.filter(item => item.categoryId === selectedCategory.id);
      }
      
      // Seçilen markaya göre filtreleme
      if (selectedBrand) {
        itemsToExport = itemsToExport.filter(item => item.brandId === selectedBrand.id);
      }
      
      // Seçilen depoya göre filtreleme
      if (selectedWarehouse) {
        itemsToExport = itemsToExport.filter(item => 
          item.warehouseStocks?.some(ws => ws.warehouseId === selectedWarehouse.id)
        );
      }
      
      // Şube filtrelemesi
      if (selectedBranch) {
        itemsToExport = itemsToExport.filter(item => 
          selectedBranch.id === "null" ? item.branchesId === null : item.branchesId === selectedBranch.id
        );
      }
      
      // Stoğu olmayan ürünleri gizle
      if (hideEmptyStock) {
        itemsToExport = itemsToExport.filter(item => {
          const totalQuantity = item.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0;
          return totalQuantity > 0;
        });
      }
      
      // Pasif ürünleri gizle
      if (hideInactiveItems) {
        itemsToExport = itemsToExport.filter(item => item.isActive);
      }
      
      // Satış fiyatı 0 olanları gizle
      if (hideZeroPrice) {
        itemsToExport = itemsToExport.filter(item => item.salePrice > 0);
      }

      // ✅ Gelişmiş filtreler (liste ile aynı semantik)
      if (minSalePrice != null) {
        itemsToExport = itemsToExport.filter(item => (item.salePrice || 0) >= minSalePrice);
      }
      if (maxSalePrice != null) {
        itemsToExport = itemsToExport.filter(item => (item.salePrice || 0) <= maxSalePrice);
      }
      if (minQuantity != null || maxQuantity != null) {
        itemsToExport = itemsToExport.filter(item => {
          const stocks = item.warehouseStocks || [];
          const total = selectedWarehouse
            ? stocks.filter(ws => ws.warehouseId === selectedWarehouse.id).reduce((s, ws) => s + ws.quantity, 0)
            : stocks.reduce((s, ws) => s + ws.quantity, 0);
          if (minQuantity != null && total < minQuantity) return false;
          if (maxQuantity != null && total > maxQuantity) return false;
          return true;
        });
      }
      if (createdFrom) {
        const from = new Date(`${createdFrom}T00:00:00.000Z`).getTime();
        itemsToExport = itemsToExport.filter(item => item.createdAt && new Date(item.createdAt).getTime() >= from);
      }
      if (createdTo) {
        const to = new Date(`${createdTo}T23:59:59.999Z`).getTime();
        itemsToExport = itemsToExport.filter(item => item.createdAt && new Date(item.createdAt).getTime() <= to);
      }
      if (ecommerceFilter !== 'all') {
        const want = ecommerceFilter === 'yes';
        itemsToExport = itemsToExport.filter(item => !!item.isEcommerce === want);
      }
      if (onlyWithoutImage) {
        itemsToExport = itemsToExport.filter(item => {
          const url = (item.imageUrl || '').trim();
          return !url;
        });
      }
      if (itemClasses.length > 0) {
        const includeNone = itemClasses.includes('NONE');
        const classes = itemClasses.filter(value => value !== 'NONE');
        itemsToExport = itemsToExport.filter(item => {
          const itemClass = item.itemClass || null;
          return (includeNone && !itemClass) || (itemClass != null && classes.includes(itemClass));
        });
      }
      if (productionMode) {
        itemsToExport = itemsToExport.filter(item => (item.productionMode || 'SALE_EXPLODE') === productionMode);
      }

      console.log('📊 [EXCEL EXPORT] Filtrelenmiş ürün sayısı:', itemsToExport.length);
      
      // ExcelService kullanarak export yap
      await ExcelService.exportToExcel(
        itemsToExport,
        getCategoryName,
        getBrandName,
        getWarehouseName,
        getBranchName,
        getCurrencySymbol,
        includeProductionColumns
      );
      
      setError(null);
      console.log(`✅ [EXCEL EXPORT] ${itemsToExport.length} adet stok ürünü Excel dosyasına aktarıldı.`);
      showSnackbar(`${itemsToExport.length} ürün başarıyla Excel'e aktarıldı`, 'success');
      
    } catch (error) {
      console.error('❌ [EXCEL EXPORT] Excel dışa aktarma hatası:', error);
      setError('Excel dışa aktarma sırasında bir hata oluştu.');
      showSnackbar('Excel dışa aktarma sırasında bir hata oluştu', 'error');
    } finally {
      setLoadingExport(false);
    }
  };

  return {
    loadingExport,
    exportToExcel,
  };
};

